import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, Tag, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { getEvento, createInscricao, validarCupom, getPagamentoConfig } from '../api';
import { loadMercadoPago } from "@mercadopago/sdk-js";

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { formatSP } from '../utils/spDate';

const isValidCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, '');
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
    let soma = 0;
    for (let i = 1; i <= 9; i++) soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.substring(9, 10))) return false;
    soma = 0;
    for (let i = 1; i <= 10; i++) soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.substring(10, 11))) return false;
    return true;
};

export default function EventDetailNovo() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [evento, setEvento] = useState(null);
    const [loading, setLoading] = useState(true);

    // Wizard Steps
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedLote, setSelectedLote] = useState(null);
    const [formData, setFormData] = useState({ nome: '', email: '', telefone: '', cpf: '' });
    const [respostas, setRespostas] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [pixData, setPixData] = useState(null);

    // Forma de Pagamento e Dados do Cartão
    const [paymentMethod, setPaymentMethod] = useState('pix'); // 'pix' ou 'credit_card'
    const [secureInstallments, setSecureInstallments] = useState('1');
    const [cardBrand, setCardBrand] = useState('');
    const [publicKey, setPublicKey] = useState('APP_USR-b1514077-7cac-4340-89b3-07a2ef7cb7c6');
    const [installmentOptions, setInstallmentOptions] = useState([]);
    const [mpInstance, setMpInstance] = useState(null);
    const [deviceId, setDeviceId] = useState('');

    // Initialize MercadoPago SDK instance
    useEffect(() => {
        if (publicKey) {
            loadMercadoPago().then(() => {
                const mp = new window.MercadoPago(publicKey);
                setMpInstance(mp);
            });
        }
    }, [publicKey]);

    // Capture device ID from MercadoPago SDK (compliance requirement)
    useEffect(() => {
        if (mpInstance && mpInstance.getDeviceInfo) {
            mpInstance.getDeviceInfo()
                .then(info => setDeviceId(info?.device_id || ''))
                .catch(() => console.warn('Device info not available'));
        }
    }, [mpInstance]);

    // Carrega chave pública do MP
    useEffect(() => {
        getPagamentoConfig().then(data => {
            if (data && data.publicKey) {
                setPublicKey(data.publicKey);
            }
        }).catch(err => console.error('Erro ao carregar publicKey do Mercado Pago:', err));
    }, []);

    // Coupon
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState(null);
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        getEvento(slug).then(data => {
            setEvento(data);
            if (data && data.lotes && data.lotes.length > 0) {
                const loteDisponivel = data.lotes.find(l => {
                    const expirado = l.data_validade && new Date(l.data_validade) < new Date();
                    const isVisible = l.visivel_publico !== 0;
                    const hasStarted = !l.data_inicio || new Date(l.data_inicio) <= new Date();
                    return l.quantidade > l.quantidade_vendida && !expirado && isVisible && hasStarted;
                });
                if (loteDisponivel) setSelectedLote(loteDisponivel);
            }
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setError('Evento não encontrado');
            setLoading(false);
        });
    }, [slug]);

    // Clean coupon if lote changes
    useEffect(() => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponError(null);
    }, [selectedLote]);

    const handleApplyCoupon = async () => {
        if (!couponCode || !selectedLote) return;
        setApplying(true);
        setCouponError(null);
        try {
            const result = await validarCupom(couponCode, selectedLote.preco, evento.id);
            if (result.success) {
                setAppliedCoupon(result.cupom);
            } else {
                setCouponError(result.error || 'Cupom inválido.');
                setAppliedCoupon(null);
            }
        } catch (err) {
            setCouponError('Erro de conexão ao validar cupom.');
            setAppliedCoupon(null);
        } finally {
            setApplying(false);
        }
    };

    // Initialize Secure Fields
    useEffect(() => {
        if (!mpInstance || (paymentMethod !== 'credit_card' && paymentMethod !== 'debit_card')) return;

        // Unmount any existing fields first
        try {
            mpInstance.fields.cardNumber?.unmount();
            mpInstance.fields.expirationDate?.unmount();
            mpInstance.fields.securityCode?.unmount();
        } catch (e) { }

        const style = {
            color: '#333',
            fontSize: '1rem',
            placeholderColor: '#999'
        };

        const cardNumber = mpInstance.fields.create('cardNumber', { placeholder: '0000 0000 0000 0000', style });
        const expirationDate = mpInstance.fields.create('expirationDate', { placeholder: 'MM/AA', style });
        const securityCode = mpInstance.fields.create('securityCode', { placeholder: 'CVV', style });

        cardNumber.mount('secure-cardNumber');
        expirationDate.mount('secure-expirationDate');
        securityCode.mount('secure-securityCode');

        cardNumber.on('binChange', (data) => {
            if (data && data.bin) {
                const precoOriginal = selectedLote ? Number(selectedLote.preco) : 0;
                const desconto = appliedCoupon ? Number(appliedCoupon.desconto) : 0;
                const amount = Math.max(0, precoOriginal - desconto);

                mpInstance.getInstallments({
                    amount: String(amount),
                    bin: data.bin
                }).then(resp => {
                    if (resp && resp.length > 0) {
                        setCardBrand(resp[0].payment_method_id);
                        const payerCosts = resp[0].payer_costs || [];
                        let opts = payerCosts.map(p => {
                            const isInterestFree = p.installment_rate === 0 || p.installments === 1;
                            let labelSuffix = p.installments === 1 ? '' : (isInterestFree ? ' sem juros' : ' com juros');
                            return {
                                installments: p.installments,
                                label: `${p.installments}x de ${p.installment_amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` + labelSuffix
                            };
                        });
                        if (opts.length === 0) {
                            opts = [{ installments: 1, label: `1x de ${amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` }];
                        }
                        setInstallmentOptions(opts);
                    }
                }).catch(() => { });
            } else {
                setCardBrand('');
                setInstallmentOptions([]);
            }
        });

        return () => {
            try {
                cardNumber.unmount();
                expirationDate.unmount();
                securityCode.unmount();
            } catch (e) { }
        };
    }, [mpInstance, paymentMethod, selectedLote, appliedCoupon]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedLote) {
            toastr.warning('Por favor, selecione um lote.');
            return;
        }

        setSubmitting(true);
        try {
            let paymentPayload = {
                payment_method: 'pix'
            };

            if ((paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && precoFinal > 0) {
                if (!mpInstance) {
                    toastr.error('Mercado Pago não inicializado.');
                    setSubmitting(false);
                    return;
                }

                try {
                    const cardToken = await mpInstance.fields.createCardToken({
                        cardholderName: String(formData.nomeCartao || formData.nome),
                        identificationType: 'CPF',
                        identificationNumber: formData.cpf.replace(/\D/g, '')
                    });

                    if (cardToken && cardToken.id) {
                        paymentPayload = {
                            payment_method: paymentMethod,
                            token: cardToken.id,
                            payment_method_id: cardBrand || 'visa',
                            installments: paymentMethod === 'debit_card' ? 1 : Number(secureInstallments)
                        };
                    } else {
                        throw new Error('Falha ao gerar token de segurança.');
                    }
                } catch (tokenErr) {
                    console.error('Erro ao processar cartão com Secure Fields:', tokenErr);
                    toastr.error('Erro na validação do cartão. Verifique os dados inseridos.');
                    setSubmitting(false);
                    return;
                }
            }

            const result = await createInscricao({
                evento_id: evento.id,
                titulo_evento: evento.titulo,
                lote_id: selectedLote.id,
                preco: selectedLote.preco,
                cupom_id: appliedCoupon ? appliedCoupon.id : null,
                desconto: appliedCoupon ? appliedCoupon.desconto : 0,
                device_id: deviceId,
                ...formData,
                respostas,
                ...paymentPayload
            });

            if (result.success) {
                if (result.approved) {
                    navigate('/sucesso');
                } else if (result.pix) {
                    setPixData(result);
                } else {
                    navigate('/sucesso');
                }
            } else {
                toastr.error(result.error || 'Erro ao realizar inscrição');
                setSubmitting(false);
            }
        } catch (err) {
            console.error(err);
            toastr.error(err.response?.data?.error || 'Erro de conexão ao processar inscrição.');
            setSubmitting(false);
        }
    };

    if (loading) return <div className="py-24 text-center text-gray-400">Carregando evento...</div>;
    if (error || !evento) return <div className="py-24 text-center text-red-500 font-semibold">{error}</div>;

    const precoOriginal = selectedLote ? Number(selectedLote.preco) : 0;
    const desconto = appliedCoupon ? Number(appliedCoupon.desconto) : 0;
    const precoFinal = Math.max(0, precoOriginal - desconto);
    const inscricoesEncerradas = evento.data_validade_evento && new Date(evento.data_validade_evento) < new Date();

    // Somente lotes vigentes (visíveis, já iniciados, não expirados e não esgotados) - os encerrados não são exibidos
    const lotesVigentes = (evento.lotes || []).filter(lote => {
        const isVisible = lote.visivel_publico !== 0;
        const hasStarted = !lote.data_inicio || new Date(lote.data_inicio) <= new Date();
        const expirado = lote.data_validade && new Date(lote.data_validade) < new Date();
        const esgotado = lote.quantidade_vendida >= lote.quantidade;
        return isVisible && hasStarted && !expirado && !esgotado;
    });

    if (pixData) {
        return (
            <div className="flex-grow pb-20">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 text-center">
                    <div className="bg-green-50 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Check size={32} />
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Inscrição Pré-Reservada!</h2>
                    <p className="text-gray-500 mb-8">Pague via Pix para garantir a sua vaga no evento.</p>

                    {pixData.qr_code_base64 && (
                        <div className="bg-white p-6 rounded-xl inline-block border border-gray-100 shadow-sm mb-8">
                            <img
                                src={`data:image/png;base64,${pixData.qr_code_base64}`}
                                alt="QR Code PIX"
                                className="max-w-[200px] block"
                            />
                        </div>
                    )}

                    <div className="max-w-md mx-auto mb-6 text-left">
                        <span className="block font-semibold mb-2 text-sm text-gray-500">Código Pix Copia e Cola:</span>
                        <textarea
                            readOnly
                            value={pixData.qr_code}
                            className="w-full h-20 resize-none text-xs font-mono p-3 mb-4 rounded-lg border border-gray-200 bg-gray-50"
                        />
                        <button
                            className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3.5 rounded-lg font-semibold transition-all"
                            onClick={() => { navigator.clipboard.writeText(pixData.qr_code); toastr.success('Pix Copiado!'); }}
                        >
                            Copiar Código Pix
                        </button>
                    </div>
                    <p className="text-sm text-gray-500">
                        A confirmação é feita automaticamente! Você receberá o ingresso em seu e-mail e poderá consultá-lo na área <strong>Minhas Inscrições</strong> em instantes.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-grow pb-20">

            <div className="w-full h-[40vh] min-h-[300px] relative">
                <img
                    src={evento.imagem_url || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1920&auto=format&fit=crop'}
                    width={1920}
                    height={800}
                    className="w-full h-full object-cover"
                    alt={evento.titulo}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 relative z-10">
                <div className="flex flex-col lg:flex-row gap-8">

                    {/* COLUNA ESQUERDA */}
                    <div className="w-full lg:w-2/3">
                        {currentStep === 2 && !inscricoesEncerradas ? (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-10">
                                <p className="text-primary-600 font-semibold text-sm mb-1">{evento.titulo}</p>
                                <h3 className="text-xl font-bold text-gray-900 mb-6">Preencha Seus Dados</h3>

                                <form id="inscricao-form" onSubmit={handleSubmit}>
                                    <div className="grid gap-x-5 gap-y-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome Completo</label>
                                            <input
                                                type="text"
                                                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                                placeholder="Ex: João Silva"
                                                value={formData.nome}
                                                onChange={e => setFormData({ ...formData, nome: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF</label>
                                            <input
                                                type="text"
                                                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                                placeholder="Ex: 000.000.000-00"
                                                value={formData.cpf}
                                                onChange={e => {
                                                    let val = e.target.value.replace(/\D/g, '');
                                                    if (val.length > 11) val = val.substring(0, 11);
                                                    if (val.length > 9) {
                                                        val = `${val.substring(0, 3)}.${val.substring(3, 6)}.${val.substring(6, 9)}-${val.substring(9)}`;
                                                    } else if (val.length > 6) {
                                                        val = `${val.substring(0, 3)}.${val.substring(3, 6)}.${val.substring(6)}`;
                                                    } else if (val.length > 3) {
                                                        val = `${val.substring(0, 3)}.${val.substring(3)}`;
                                                    }
                                                    setFormData({ ...formData, cpf: val });
                                                }}
                                                onBlur={e => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    if (val.length > 0 && !isValidCPF(val)) {
                                                        toastr.warning('O CPF informado parece ser inválido. Por favor, verifique.');
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">WhatsApp (Telefone)</label>
                                            <input
                                                type="text"
                                                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                                placeholder="Ex: (18) 99999-9999"
                                                value={formData.telefone}
                                                onChange={e => setFormData({ ...formData, telefone: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">E-mail</label>
                                            <input
                                                type="email"
                                                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                                placeholder="Ex: joao@email.com"
                                                value={formData.email}
                                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {/* Campos Personalizados */}
                                    {evento.campos_personalizados && (() => {
                                        let campos = [];
                                        try { campos = typeof evento.campos_personalizados === 'string' ? JSON.parse(evento.campos_personalizados) : evento.campos_personalizados; } catch (e) { }
                                        if (campos.length > 0) {
                                            return (
                                                <div className="mt-8 border-t border-gray-100 pt-6">
                                                    <h4 className="font-semibold text-gray-900 mb-4">Informações Adicionais</h4>
                                                    <div className="grid gap-x-5 gap-y-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                                                        {campos.map((campo, idx) => (
                                                            <div key={idx} style={{ gridColumn: campo.tipo === 'textarea' ? '1 / -1' : 'auto' }}>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1.5">{campo.nome}</label>
                                                                {campo.tipo === 'textarea' ? (
                                                                    <textarea
                                                                        className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                        required
                                                                        rows="3"
                                                                        placeholder={campo.placeholder || ''}
                                                                        value={respostas[campo.nome] || ''}
                                                                        onChange={e => setRespostas({ ...respostas, [campo.nome]: e.target.value })}
                                                                    />
                                                                ) : (
                                                                    <input
                                                                        type={campo.tipo || 'text'}
                                                                        className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                        required
                                                                        placeholder={campo.placeholder || ''}
                                                                        value={respostas[campo.nome] || ''}
                                                                        onChange={e => setRespostas({ ...respostas, [campo.nome]: e.target.value })}
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}

                                    {/* Forma de Pagamento (apenas se for pago) */}
                                    {precoFinal > 0 && (
                                        <div className="mt-10 border-t border-gray-100 pt-6">
                                            <h4 className="font-semibold text-gray-900 mb-4">Forma de Pagamento</h4>

                                            <div className="grid gap-2 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                                                <div
                                                    onClick={() => setPaymentMethod('pix')}
                                                    className={`py-2.5 px-3 rounded-lg text-center text-sm font-semibold cursor-pointer transition-all border flex items-center justify-center gap-1.5 ${paymentMethod === 'pix' ? 'border-2 border-primary-600 bg-primary-50' : 'border-gray-200 bg-white'}`}
                                                >
                                                    ⚡ Pix
                                                </div>
                                                <div
                                                    onClick={() => setPaymentMethod('credit_card')}
                                                    className={`py-2.5 px-3 rounded-lg text-center text-sm font-semibold cursor-pointer transition-all border flex items-center justify-center gap-1.5 ${paymentMethod === 'credit_card' ? 'border-2 border-primary-600 bg-primary-50' : 'border-gray-200 bg-white'}`}
                                                >
                                                    💳 Crédito
                                                </div>
                                                <div
                                                    onClick={() => setPaymentMethod('debit_card')}
                                                    className={`py-2.5 px-3 rounded-lg text-center text-sm font-semibold cursor-pointer transition-all border flex items-center justify-center gap-1.5 ${paymentMethod === 'debit_card' ? 'border-2 border-primary-600 bg-primary-50' : 'border-gray-200 bg-white'}`}
                                                >
                                                    💳 Débito
                                                </div>
                                            </div>

                                            {(paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && (
                                                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                                                    <h5 className="font-bold text-gray-900 mb-5">
                                                        Dados do Cartão de {paymentMethod === 'credit_card' ? 'Crédito' : 'Débito'}
                                                    </h5>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <div className="sm:col-span-2">
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Nome Impresso no Cartão</label>
                                                            <input
                                                                type="text"
                                                                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                placeholder="Ex: JOAO SILVA"
                                                                required={paymentMethod === 'credit_card' || paymentMethod === 'debit_card'}
                                                                value={formData.nomeCartao || ''}
                                                                onChange={e => setFormData({ ...formData, nomeCartao: e.target.value.toUpperCase() })}
                                                            />
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Número do Cartão</label>
                                                            <div className="flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden">
                                                                <div id="secure-cardNumber" className="flex-1 px-3" style={{ height: '44px' }}></div>
                                                                <span className="px-3 text-xs font-bold uppercase text-gray-500 border-l border-gray-200 min-w-[70px] text-center">
                                                                    {cardBrand || 'Cartão'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Validade (MM/AA)</label>
                                                            <div id="secure-expirationDate" className="w-full rounded-lg border border-gray-200 bg-white px-3" style={{ height: '44px' }}></div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Código (CVV)</label>
                                                            <div id="secure-securityCode" className="w-full rounded-lg border border-gray-200 bg-white px-3" style={{ height: '44px' }}></div>
                                                        </div>

                                                        {paymentMethod === 'credit_card' && (
                                                            <div className="sm:col-span-2">
                                                                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Opções de Parcelamento</label>
                                                                <select
                                                                    className="w-full rounded-lg border border-gray-200 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                    value={secureInstallments}
                                                                    onChange={e => setSecureInstallments(e.target.value)}
                                                                >
                                                                    {installmentOptions.length > 0 ? (
                                                                        installmentOptions.map(opt => (
                                                                            <option key={opt.installments} value={opt.installments}>
                                                                                {opt.label}
                                                                            </option>
                                                                        ))
                                                                    ) : (
                                                                        <option value="1">1x de {precoFinal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</option>
                                                                    )}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Ações */}
                                    <div className="flex mt-8 pt-4 border-t border-gray-100">
                                        <button
                                            type="button"
                                            className="px-6 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
                                            onClick={() => setCurrentStep(1)}
                                        >
                                            <ArrowLeft size={18} /> Voltar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-10">
                                {evento.destaque ? (
                                    <span className="bg-red-50 text-red-600 text-xs font-bold uppercase tracking-wider py-1.5 px-3 rounded-full mb-4 inline-block">Destaque</span>
                                ) : null}

                                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 tracking-tight">{evento.titulo}</h1>

                                <div className={`grid grid-cols-1 ${evento.local ? 'md:grid-cols-2' : ''} gap-6 mb-10 pb-10 border-b border-gray-100`}>
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 text-xl shrink-0">
                                            <Calendar size={20} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800">Data e Hora</h4>
                                            <p className="text-gray-500 text-sm mt-1">{formatSP(evento.data_evento)}</p>
                                        </div>
                                    </div>
                                    {evento.local && (
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 text-xl shrink-0">
                                                <MapPin size={20} />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800">Local</h4>
                                                <p className="text-gray-500 text-sm mt-1">{evento.local}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-4">Sobre o Evento</h3>
                                <div className="prose prose-gray max-w-none text-gray-600 font-light leading-relaxed">
                                    {(evento.descricao || '').split('\\n').map((par, i) => (
                                        <p key={i} className="mb-3 last:mb-0">{par}</p>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* COLUNA DIREITA (sticky) */}
                    <div className="w-full lg:w-1/3">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-24">
                            {inscricoesEncerradas ? (
                                <div className="text-center py-4">
                                    <h3 className="text-red-500 font-extrabold text-lg mb-2">Inscrições Encerradas</h3>
                                    <p className="text-gray-500 text-sm">O prazo para inscrições neste evento já expirou.</p>
                                </div>
                            ) : currentStep === 1 ? (
                                <>
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 border-b border-gray-100 pb-4">Realizar Inscrição</h3>

                                    {lotesVigentes.length > 0 ? (
                                        <div className="mb-4">
                                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Lote</label>
                                            <select
                                                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                value={selectedLote?.id || ''}
                                                onChange={e => setSelectedLote(lotesVigentes.find(l => l.id === Number(e.target.value)) || null)}
                                            >
                                                {lotesVigentes.map(lote => (
                                                    <option key={lote.id} value={lote.id}>
                                                        {lote.nome} — {Number(lote.preco) === 0 ? 'Grátis' : Number(lote.preco).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                    </option>
                                                ))}
                                            </select>

                                            {selectedLote && (
                                                <div className="flex items-center justify-between bg-primary-50 rounded-lg px-3 py-2.5 mt-2">
                                                    <span className="text-lg font-extrabold text-primary-600">
                                                        {Number(selectedLote.preco) === 0 ? 'Grátis' : Number(selectedLote.preco).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                    </span>
                                                    <span className="text-xs text-gray-500 font-medium">Restam {selectedLote.quantidade - selectedLote.quantidade_vendida} vagas</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-sm mb-4">Nenhum lote disponível no momento.</p>
                                    )}

                                    {/* Cupom (apenas se o lote selecionado for pago) */}
                                    {selectedLote && Number(selectedLote.preco) > 0 && (
                                        <div className="border-t border-gray-100 pt-4 mt-4">
                                            <h4 className="font-semibold text-gray-900 text-sm mb-2">Cupom de Desconto</h4>
                                            <div className="flex">
                                                <input
                                                    type="text"
                                                    className="flex-1 min-w-0 rounded-l-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    placeholder="Ex: PROMO10"
                                                    value={couponCode}
                                                    onChange={e => setCouponCode(e.target.value)}
                                                    disabled={!!appliedCoupon}
                                                />
                                                {appliedCoupon ? (
                                                    <button
                                                        type="button"
                                                        className="bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-3 rounded-r-lg"
                                                        onClick={() => { setAppliedCoupon(null); setCouponCode(''); }}
                                                    >
                                                        Remover
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="bg-amber-400 hover:bg-amber-500 text-gray-900 text-sm font-bold px-3 rounded-r-lg disabled:opacity-60"
                                                        onClick={handleApplyCoupon}
                                                        disabled={applying || !couponCode}
                                                    >
                                                        {applying ? '...' : 'Aplicar'}
                                                    </button>
                                                )}
                                            </div>
                                            {appliedCoupon && (
                                                <p className="text-green-600 text-xs font-semibold mt-2">
                                                    ✓ Cupom "{appliedCoupon.codigo}" aplicado! -{Number(appliedCoupon.desconto).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                </p>
                                            )}
                                            {couponError && <p className="text-red-500 text-xs mt-2">{couponError}</p>}
                                        </div>
                                    )}

                                    <button
                                        className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={!selectedLote}
                                        onClick={() => setCurrentStep(2)}
                                    >
                                        Continuar Inscrição <ArrowRight size={16} />
                                    </button>

                                    <p className="text-center text-xs text-gray-400 mt-4">🔒 Pagamento 100% seguro</p>
                                </>
                            ) : (
                                <>
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 border-b border-gray-100 pb-4">Resumo da Inscrição</h3>

                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-600">Lote</span>
                                        <span className="font-semibold text-gray-900">{selectedLote?.nome}</span>
                                    </div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-600">Valor</span>
                                        <span className="text-gray-900">{precoOriginal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                    </div>
                                    {appliedCoupon && (
                                        <div className="flex justify-between text-sm mb-2 text-green-600">
                                            <span>Cupom ({appliedCoupon.codigo})</span>
                                            <span>- {desconto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center border-t border-gray-100 mt-3 pt-3">
                                        <span className="text-gray-700 font-medium">Total</span>
                                        <span className="text-2xl font-extrabold text-primary-600">
                                            {precoFinal === 0 ? 'Grátis' : precoFinal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </span>
                                    </div>

                                    <button
                                        type="submit"
                                        form="inscricao-form"
                                        className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-50"
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Processando...' : (
                                            <>
                                                <Tag size={16} /> Finalizar e Pagar
                                            </>
                                        )}
                                    </button>

                                    <p className="text-center text-xs text-gray-400 mt-4">🔒 Pagamento 100% seguro</p>
                                </>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
